import React from "react";
import { connect } from "react-redux";
import { isEqual, isEmpty } from "lodash";
import { bindActionCreators } from "redux";
import { AgGridReact } from "ag-grid-react";
import { Header, Button } from "semantic-ui-react";
import csvDownload from "json-to-csv-export";
import { toast } from "react-semantic-toasts";

import {
    exportInventoryHistory,
    loadInventoryHistoryPage,
    changeInventoryHistoryPage,
    changeInventoryHistoryState,
} from "./actions";
import GridPageSizePicker from "../../common/components/grid-page-size-picker";
import { format } from "date-fns";
import { DatesRangeInput } from "semantic-ui-calendar-react";
import { DateFormat } from "utilities/dist/invoice/constants";

const NoInventoriesOverlay = () => {
    return (
        <div className="tm-20">
            <Header>No inventory records found.</Header>
        </div>
    );
};

class DataGrid extends React.Component {
    state = {
        isExporting: false,
        dateRange: "",
    };

    grid = null;
    gridDataSource = {
        getRows: params => {
            this.props.loadInventoryHistoryPage(this.props, params);
        },
    };

    componentWillUnmount() {
        this.grid = null;
    }

    onGridReady = grid => {
        this.grid = grid;

        setTimeout(() => this.grid && this.grid.api.sizeColumnsToFit(), 300);
        this.grid.api.setDatasource(this.gridDataSource);
        this.grid.columnApi.applyColumnState({ state: this.props.sortModel, defaultState: {} });
        this.grid.api.setFilterModel(this.props.filterModel);

        this.grid.api.addEventListener("sortChanged", () => {
            const colStates = this.grid.columnApi.getColumnState();
            const sortModel = colStates
                .filter(colState => colState.sort)
                .map(colState => ({
                    colId: colState.colId,
                    sort: colState.sort,
                }));

            this.props.changeInventoryHistoryState({
                sortModel,
            });
        });

        this.grid.api.addEventListener("filterChanged", () => {
            this.props.changeInventoryHistoryState({
                filterModel: this.grid.api.getFilterModel(),
            });
        });
    };

    reloadGridData = () => {
        this.grid && this.grid.api.refreshInfiniteCache();
    };

    changePage = () => {
        if (this.grid) {
            const page = this.grid.api.paginationGetCurrentPage() + 1;

            if (page === this.props.currentPage) return;

            this.props.changeInventoryHistoryPage(page);
        }
    };

    componentDidUpdate(prevProps) {
        if (!this.grid) return;

        if (prevProps.isLoading && !this.props.isLoading) {
            this.grid.api.hideOverlay();
        }

        if (!prevProps.isLoading && this.props.isLoading) {
            this.grid.api.showLoadingOverlay();
        }

        if (!this.props.isLoading && this.props.inventories.length < 1) {
            this.grid.api.showNoRowsOverlay();
        }

        if (!isEqual(prevProps.filterModel, this.props.filterModel)) {
            this.grid.api.setFilterModel(this.props.filterModel);
        }
    }

    export = () => {
        this.setState({ isExporting: true });
        this.props
            .exportInventoryHistory(this.props)
            .then(inventoryEntries => {
                csvDownload(
                    inventoryEntries,
                    `inventory_history_${format(new Date(), "yyy_MM_dd")}.csv`
                );
            })
            .catch(err => {
                toast({
                    time: 30000,
                    type: "error",
                    icon: "clock",
                    title: "Error exporting inventory history",
                    list: [`Failed to export inventory history data!`, `Details: ${err.message}.`],
                });
            })
            .finally(() => this.setState({ isExporting: false }));
    };

    handleDateRangeChange = (e, { value }) => {
        const filterModel = { ...this.props.filterModel };
        const [start, end] = value?.split(" - ");

        if (start && this.grid) {
            filterModel.created_at = {
                type: "inRange",
                filterType: "date",
                dateTo: format(new Date(end || start), "yyyy-MM-dd"),
                dateFrom: format(new Date(start), "yyyy-MM-dd"),
            };
        } else if (filterModel.created_at) {
            delete filterModel["created_at"];
        }

        this.props.changeInventoryHistoryState({ filterModel });
        this.setState({ dateRange: value });
    };

    render() {
        return (
            <>
                <div className="clear bm-10">
                    <GridPageSizePicker
                        button
                        compact
                        pointing="top"
                        pageSize={this.props.pageSize}
                        className="right floated basic"
                        text={`${this.props.pageSize}/page`}
                        onChange={this.props.changeInventoryHistoryState}
                    />

                    {!isEmpty(this.props.filterModel) && (
                        <Button
                            size="mini"
                            icon="filter"
                            floated="right"
                            content="Clear Filters"
                            onClick={() => this.grid.api.setFilterModel({})}
                        />
                    )}

                    <Button size="mini" floated="right" content="Export" onClick={this.export} />
                    <DatesRangeInput
                        size="mini"
                        className="fr"
                        clearable={true}
                        animation={null}
                        maxDate={new Date()}
                        placeholder="From - To"
                        value={this.state.dateRange}
                        dateFormat={DateFormat.toUpperCase()}
                        onChange={this.handleDateRangeChange}
                    />
                </div>
                <div className="ag-theme-balham bm-10 clear inventory-grid">
                    <AgGridReact
                        pagination
                        rowHeight={50}
                        paginationPageSize={this.props.pageSize}
                        rowSelection="single"
                        domLayout="autoHeight"
                        rowModelType="infinite"
                        onGridReady={this.onGridReady}
                        columnDefs={this.props.columnDefs}
                        cacheBlockSize={this.props.pageSize}
                        onPaginationChanged={this.changePage}
                        noRowsOverlayComponent={NoInventoriesOverlay}
                        defaultColDef={{
                            resizable: true,
                            sortable: true,
                            floatingFilter: true,
                            wrapHeaderText: true,
                            autoHeaderHeight: true,
                        }}
                    />
                </div>
            </>
        );
    }
}

const mapStateToProps = ({ inventoryHistory, auth }) => ({
    company: auth.user.company,

    pageSize: inventoryHistory.pageSize,
    isLoading: inventoryHistory.isLoading,
    sortModel: inventoryHistory.sortModel,
    columnDefs: inventoryHistory.columnDefs,
    filterModel: inventoryHistory.filterModel,
    currentPage: inventoryHistory.currentPage,
    inventories: inventoryHistory.inventories,
    showCellEditConfirmation: inventoryHistory.showCellEditConfirmation,
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            exportInventoryHistory,
            loadInventoryHistoryPage,
            changeInventoryHistoryState,
            changeInventoryHistoryPage: page => dispatch(changeInventoryHistoryPage(page)),
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(DataGrid);
